import React from 'react';
import styled from 'styled-components';
import Vendor from 'data/Vendor';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models, service } from 'powerbi-client';
import FeatureFlag from 'data/FeatureFlag';
import usePathState from 'hooks/usePathState';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Breadcrumbs from 'components/UI/Breadcrumb';
import { usePath } from 'hooks/useHookRouterCompat';
import useBreadcrumbs from 'hooks/useBreadcrumbs';
import Tabs from 'components/UI/Tabs';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { FontWeight } from 'lib/styles';
import { usePowerBiReports } from 'hooks/usePowerBiReports';
import { GENERAL_CONSTANTS } from 'constants/general';
import { BusinessUnit } from 'data/BusinessUnit';

const Page = styled.div`
  width: 100%;
`;

const Title = styled.h3`
  font-weight: ${FontWeight.medium};
  font-size: 24px;
  line-height: 28px;
  color: #0e141a;
`;

const Container = styled.div`
  padding: 6px 24px;
  .report-style-class {
    width: 100%;
    height: 100vh;
  }
  iframe {
    border: none;
  }
`;

interface Props {
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  featureFlags: FeatureFlag;
  vendorsList: Vendor[];
}

const PowerBiReports = ({ selectedVendor, featureFlags, vendorsList, selectedBusinessUnit }: Props) => {
  const { currentPath } = usePathState();
  const path = usePath();
  const crumbs = useBreadcrumbs(path);

  // Custom hook for all Power BI related logic
  const {
    powerBiTabs,
    activeTab,
    setActiveTab,
    reportInfo,
    reports
  } = usePowerBiReports({
    selectedVendor,
    selectedBusinessUnit,
    featureFlags,
    vendorsList,
  });

  const breadcrumbs = currentPath.includes('reports') ? (
    <FlexboxContainer padding="10px 14px" flexDirection="row">
      <Breadcrumbs containerPadding="0px" crumbs={crumbs} />
    </FlexboxContainer>
  ) : (
    <Breadcrumbs crumbs={crumbs} />
  );

  return featureFlags.powerBiReport && featureFlags.powerBiReport.enabled ? (
    <Page>
      {breadcrumbs}
      <Container>
        <Title>{GENERAL_CONSTANTS.REPORTS}</Title>
        <FlexboxContainerComponent margin="33px 0px" flexDirection="column">
          <Tabs
            setActiveTab={setActiveTab}
            height="auto"
            tabs={powerBiTabs}
            activeTab={activeTab}
            theme="brandLight"
            borderRadius="20px"
            margin="0 24px 8px 0"
            tabPanelPadding="0 !important"
            className='brands-tabs'
            disabledTabTooltip={GENERAL_CONSTANTS.FEATURE_DISABLE_FOR_ROLE}
          >
            {reports.map(() => (
              <PowerBIEmbed
                key={reportInfo['Vendor']}
                embedConfig={{
                  type: 'report',
                  id: reportInfo.ReportId,
                  embedUrl: reportInfo.EmbedUrl,
                  accessToken: reportInfo.EmbedToken,
                  tokenType: models.TokenType.Embed,
                  permissions: models.Permissions.All,
                  settings: {
                    filterPaneEnabled: true,
                    navContentPaneEnabled: true,
                  },
                }}
                eventHandlers={
                  new Map([
                    [
                      'loaded',
                      function () {
                        console.log('Report loaded');
                      },
                    ],
                    [
                      'rendered',
                      function () {
                        console.log('Report rendered');
                      },
                    ],
                    [
                      'error',
                      function (event?: service.ICustomEvent<any>) {
                        if (event) {
                          console.log(event.detail);
                        }
                      },
                    ],
                  ])
                }
                cssClassName={'report-style-class'}
              />
            ))}
          </Tabs>
        </FlexboxContainerComponent>
      </Container>
    </Page>
  ) : null;
};

export default PowerBiReports;
